import React, { useState } from 'react';
import { Card, Switch, Typography, Space, Tag } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import PerFieldProvideField from './PerFieldProvideField';
import styles from './OptionalCollapsibleField.module.scss';
const isFieldActive = (value) => {
    return !(value === undefined ||
        value === null ||
        value === '' ||
        (typeof value === 'object' && value !== null && Object.keys(value).length === 0 && !('##provide' in value) && !('##literal' in value)) ||
        (Array.isArray(value) && value.length === 0));
};
const hasRequiredChildren = (schema) => {
    if (!schema || typeof schema !== 'object')
        return false;
    // Check direct required fields
    if (schema.required && Array.isArray(schema.required) && schema.required.length > 0) {
        return true;
    }
    // Check properties for nested required fields
    if (schema.properties && typeof schema.properties === 'object') {
        return Object.values(schema.properties).some((propSchema) => hasRequiredChildren(propSchema));
    }
    // Check oneOf/allOf for required fields
    if (schema.oneOf && Array.isArray(schema.oneOf)) {
        return schema.oneOf.some((subSchema) => hasRequiredChildren(subSchema));
    }
    if (schema.allOf && Array.isArray(schema.allOf)) {
        return schema.allOf.some((subSchema) => hasRequiredChildren(subSchema));
    }
    return false;
};
const isOptionalField = (schema, parentSchema, fieldName) => {
    var _a;
    // Check if this field is optional in its parent
    const isOptional = !((_a = parentSchema === null || parentSchema === void 0 ? void 0 : parentSchema.required) === null || _a === void 0 ? void 0 : _a.includes(fieldName));
    // Don't apply to enum fields as they're handled well by default selects
    if (schema.enum) {
        return false;
    }
    // Don't apply to const fields as they're hidden
    if (schema.const !== undefined) {
        return false;
    }
    return isOptional;
};
const OptionalCollapsibleField = (props) => {
    const { t } = useTranslation();
    const { schema, name, formData, uiSchema, registry } = props;
    const parentSchema = uiSchema === null || uiSchema === void 0 ? void 0 : uiSchema.parentSchema;
    const shouldCollapse = isOptionalField(schema, parentSchema, name);
    const fieldIsActive = isFieldActive(formData);
    // Track if field has been explicitly enabled by user (separate from having active value)
    const [isExplicitlyEnabled, setIsExplicitlyEnabled] = useState(fieldIsActive);
    // Sync isExplicitlyEnabled with fieldIsActive on first render or when field gets real data
    React.useEffect(() => {
        if (fieldIsActive) {
            setIsExplicitlyEnabled(true);
        }
    }, [fieldIsActive]);
    // Field should be shown as active if it has real data OR user explicitly enabled it
    const shouldShowAsActive = fieldIsActive || isExplicitlyEnabled;
    // Keep collapse state in sync with shouldShowAsActive
    React.useEffect(() => {
        setIsCollapsed(!shouldShowAsActive);
    }, [shouldShowAsActive]);
    const [isCollapsed, setIsCollapsed] = useState(false); // Will be set correctly by useEffect
    const handleToggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };
    const handleActivateField = (checked) => {
        if (checked) {
            // User wants to enable the field
            setIsExplicitlyEnabled(true);
            setIsCollapsed(false);
            // If field doesn't have meaningful data yet, set appropriate empty value
            if (!fieldIsActive) {
                let defaultValue;
                if (schema.type === 'string') {
                    defaultValue = '';
                }
                else if (schema.type === 'number') {
                    defaultValue = 0;
                }
                else if (schema.type === 'integer') {
                    defaultValue = 0;
                }
                else if (schema.type === 'boolean') {
                    defaultValue = false;
                }
                else if (schema.type === 'array') {
                    defaultValue = [];
                }
                else if (schema.type === 'object' || schema.oneOf || schema.allOf) {
                    defaultValue = {};
                }
                else {
                    defaultValue = '';
                }
                props.onChange(defaultValue);
            }
        }
        else {
            // User wants to disable the field
            setIsExplicitlyEnabled(false);
            props.onChange(undefined);
            setIsCollapsed(true);
        }
    };
    if (!shouldCollapse) {
        // For required fields or fields that shouldn't be collapsed, use the standard field
        return React.createElement(PerFieldProvideField, Object.assign({}, props));
    }
    const getFieldLabel = () => {
        const baseLabel = schema.title || name;
        return (React.createElement(Space, null,
            React.createElement(Typography.Text, { strong: true }, baseLabel),
            React.createElement(Tag, { color: "blue", style: { fontSize: '10px', padding: '0 4px', height: '18px', lineHeight: '16px' } },
                React.createElement(Trans, { i18nKey: "optionalTag", defaults: "optional" }))));
    };
    const getRequiredFieldsList = (schema) => {
        const required = [];
        if (schema.required && Array.isArray(schema.required)) {
            required.push(...schema.required);
        }
        if (schema.properties && typeof schema.properties === 'object') {
            Object.entries(schema.properties).forEach(([key, propSchema]) => {
                const nestedRequired = getRequiredFieldsList(propSchema);
                required.push(...nestedRequired.map(field => `${key}.${field}`));
            });
        }
        return required;
    };
    if (!shouldShowAsActive) {
        const requiredFields = getRequiredFieldsList(schema);
        const hasRequiredFieldsList = requiredFields.length > 0;
        // Get field type info for display
        const getFieldTypeInfo = () => {
            if (schema.type === 'object' && schema.properties) {
                const propCount = Object.keys(schema.properties).length;
                return propCount === 1
                    ? t('fieldTypeObjectSingle', 'Object with {{count}} property', { count: propCount })
                    : t('fieldTypeObjectPlural', 'Object with {{count}} properties', { count: propCount });
            }
            if (schema.type === 'array') {
                return t('fieldTypeArray', 'Array/List field');
            }
            if (schema.oneOf || schema.allOf) {
                return t('fieldTypeComplex', 'Complex field with multiple options');
            }
            if (schema.type === 'string') {
                return t('fieldTypeText', 'Text field');
            }
            if (schema.type === 'number') {
                return t('fieldTypeNumber', 'Number field');
            }
            if (schema.type === 'integer') {
                return t('fieldTypeInteger', 'Integer field');
            }
            if (schema.type === 'boolean') {
                return t('fieldTypeBoolean', 'Boolean field');
            }
            return t('fieldTypeGeneric', 'Field');
        };
        return (React.createElement(Card, { size: "small", className: styles.optionalCard, style: {
                marginBottom: 16,
                border: '1px dashed #d9d9d9',
                backgroundColor: '#fafafa'
            } },
            React.createElement("div", { className: styles.optionalHeader }, getFieldLabel()),
            schema.description && (React.createElement(Typography.Text, { type: "secondary", style: { fontSize: '12px', display: 'block', marginBottom: 8 } }, schema.description)),
            React.createElement(Typography.Text, { type: "secondary", style: { fontSize: '11px', display: 'block', marginBottom: hasRequiredFieldsList ? 4 : 12 } }, getFieldTypeInfo()),
            hasRequiredFieldsList && (React.createElement(Typography.Text, { type: "secondary", style: { fontSize: '11px', display: 'block', marginBottom: 12 } }, t('requiredWhenEnabled', 'Required when enabled: {{fields}}', { fields: requiredFields.join(', ') }))),
            React.createElement(Switch, { checked: false, onChange: handleActivateField, checkedChildren: t('enabledLabel', 'Enabled'), unCheckedChildren: t('enableLabel', 'Enable'), size: "small" })));
    }
    return (React.createElement(Card, { size: "small", className: styles.activeOptionalCard, style: {
            marginBottom: 16,
            border: '1px solid #1890ff',
            backgroundColor: '#f6ffed'
        } },
        React.createElement("div", null,
            React.createElement("div", { className: styles.activeOptionalHeader, onClick: handleToggleCollapse, style: { cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
                React.createElement("div", { style: { display: 'flex', alignItems: 'center' } },
                    isCollapsed ? React.createElement(RightOutlined, null) : React.createElement(DownOutlined, null),
                    React.createElement("span", { style: { marginLeft: 8 } }, getFieldLabel())),
                React.createElement(Switch, { checked: shouldShowAsActive, onChange: handleActivateField, checkedChildren: t('enabledLabel', 'Enabled'), unCheckedChildren: t('disabledLabel', 'Disabled'), size: "small", onClick: (checked, e) => e.stopPropagation() })),
            schema.description && (React.createElement(Typography.Text, { type: "secondary", style: { fontSize: '12px', display: 'block', marginTop: 8, marginBottom: 8 } }, schema.description))),
        !isCollapsed && (React.createElement("div", { style: { marginTop: 16 } },
            React.createElement(PerFieldProvideField, Object.assign({}, props, { schema: Object.assign(Object.assign({}, props.schema), { description: undefined // Remove description from schema to prevent RJSF from rendering it
                 }), uiSchema: Object.assign(Object.assign({}, props.uiSchema), { 'ui:description': undefined // Remove description from uiSchema too
                 }) }))))));
};
export default OptionalCollapsibleField;
